import * as React from "react";
import Axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Dispatch, useContext } from "react";
import { Col, Row } from "react-bootstrap";
import { List, ListItem, ListItemIcon, ListItemText, Popover } from "@material-ui/core";
import { Link, useHistory, useLocation } from "react-router-dom";
import useIsMounted from "ismounted";
import CommonApis from "../apis/common.apis";
import UserAPIs, { invalidateUserSignatureCache } from "../apis/user.apis";
import { getMenuNameFromPath, getSubMenuFromMainMenu, isSubMenuActive } from "../components/common";
import { useTranslation } from "react-i18next";
import logo from "../assets/images/logo-tilted-small.png";
import bannerSide from "../assets/images/bannerSide.png";
import playStore from "../assets/images/playStore.png";
import { AppDispatchContext, AppStateContext } from "../App";
import { Add, ExpandMore, OpenInNew } from "@material-ui/icons";
import classNames from "classnames";

import { iStoreAction } from "../redux/reducers";
import ShopSwitcherLogoOrInitials from "../components/ShopSwitcher/ShopSwitcherLogoOrInitials";
import type { iShopifyShop } from "../types/internal";
import { clearProductCache } from "./Dashboard/MicroComponent/MicroComp";

function ListItemLink(props: any) {
    return <ListItem button component={Link} {...props} />;
}

function Sidebar(props: any): any {
    const location = useLocation();
    const history = useHistory()
    const { t: tNav } = useTranslation("nav");
    const { t: tCommon } = useTranslation("common");
    const { t: tChats } = useTranslation("chats");
    const { shop, shops, loggedInUser } = useContext(AppStateContext);
    const opened_menu: string = getMenuNameFromPath(location.pathname, 1);
    const links = getSubMenuFromMainMenu(opened_menu, tNav, loggedInUser?.shop_permissions);
    const logoImage = { width: '80%', marginTop: '-16px' };
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [hideServer , setHideServer] = useState(true);
    const [signatureLogoUrl, setSignatureLogoUrl] = useState<string | null>(null);
    const [chatPromoCollapsed, setChatPromoCollapsed] = useState(false);
    const isMounted = useIsMounted();

    const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);

    const loadShopSignatureLogo = useCallback(() => {
        if (!shop?.uid) {
            setSignatureLogoUrl(null);
            return;
        }
        new UserAPIs().fetch_user_signature().then((res) => {
            if (!isMounted.current) return;
            const u = res?.signature?.logo_url;
            setSignatureLogoUrl(typeof u === "string" && u.trim() !== "" ? u.trim() : null);
        });
    }, [shop?.uid, isMounted]);

    useEffect(() => {
        loadShopSignatureLogo();
    }, [loadShopSignatureLogo]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {

        setAnchorEl(null);
    };

    useEffect(()=>{
        if(shop?.customer?.plan?.options){
            let options = JSON.parse(shop?.customer?.plan?.options);
            if( options && options.sending_server_option != 'system'){
                setHideServer(false);
            } else{
                setHideServer(true);
            }
        }
    }, [shop])

    useEffect(() => {
        const sidebarEl = document.getElementById("sidebar");
        if (!sidebarEl) return;
        sidebarEl.classList.toggle("sidebar-chat-promo-expanded", !chatPromoCollapsed);
        return () => {
            sidebarEl.classList.remove("sidebar-chat-promo-expanded");
        };
    }, [chatPromoCollapsed]);

    return <div className="wrapper">
        <div id="sidebar">
            <Row className="sidebar-row">
                <Col md={12} xs={12} className="d-flex align-items-center justify-content-center logo-parent-div">
                    <div className="sidebar-header">
                        <div className="logo" >
                            <img src={logo} alt="Emailwish Logo" style={logoImage} />
                        </div>
                    </div>
                </Col>
                <Col md={12} xs={12} className="d-flex m-2 align-items-center justify-content-center dropdown-parent-div">
                {
                    <div className="selector-div mt-3 ew-shop-switcher">
                        <button
                            type="button"
                            className="ew-shop-switcher__trigger"
                            aria-haspopup="menu"
                            aria-expanded={Boolean(anchorEl)}
                            aria-controls="ew-shop-switcher-menu"
                            id="ew-shop-switcher-trigger"
                            onClick={handleClick}
                        >
                            <span className="ew-shop-switcher__avatar-slot">
                                {shop ? (
                                    <ShopSwitcherLogoOrInitials
                                        name={shop.name}
                                        colorKey={
                                            shop.shop_owner_email ||
                                            shop.myshopify_domain ||
                                            String(shop.uid ?? "")
                                        }
                                        logoUrl={signatureLogoUrl}
                                        variant="trigger"
                                    />
                                ) : (
                                    <span
                                        className="ew-shop-switcher__initials ew-shop-switcher__initials--trigger ew-shop-switcher__initials--placeholder"
                                        aria-hidden
                                    >
                                        ?
                                    </span>
                                )}
                            </span>
                            <span className="ew-shop-switcher__name" title={shop?.name || undefined}>
                                {shop?.name || "—"}
                            </span>
                            <ExpandMore
                                className={classNames(
                                    "ew-shop-switcher__chevron",
                                    Boolean(anchorEl) && "ew-shop-switcher__chevron--open"
                                )}
                                aria-hidden
                            />
                        </button>
                        <div className="ew-shop-switcher__divider" role="presentation" />
                        {shop && (
                            <Popover
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                                transformOrigin={{ vertical: "top", horizontal: "center" }}
                                onClose={handleClose}
                                PaperProps={{
                                    className: "ew-header-menu-paper",
                                    elevation: 0,
                                }}
                                getContentAnchorEl={null}
                                marginThreshold={8}
                            >
                                <nav
                                    id="ew-shop-switcher-menu"
                                    className="ew-dropdown-panel ew-dropdown-panel--shop-list"
                                    role="menu"
                                    aria-labelledby="ew-shop-switcher-trigger"
                                >
                                    {shops?.map((s: iShopifyShop) => {
                                        const active = shop?.uid === s.uid;
                                        const storeUrl =
                                            s.myshopify_domain &&
                                            `https://${s.myshopify_domain}`;
                                        return (
                                            <div
                                                key={s.uid || s.name}
                                                className={classNames(
                                                    "ew-shop-switcher__row",
                                                    active && "ew-shop-switcher__row--active"
                                                )}
                                                role="none"
                                            >
                                                <button
                                                    type="button"
                                                    role="menuitem"
                                                    className="ew-shop-switcher__shop-btn"
                                                    onClick={async () => {
                                                        try {
                                                            const customerUid = s.customer_uid || s.uid;
                                                            await Axios.post(process.env.REACT_APP_BACKEND_API_URL + `/switch/shop`, {
                                                                selected_customer_uid: customerUid,
                                                            }, {
                                                                withCredentials: true,
                                                            });
                                                            invalidateUserSignatureCache();
                                                            clearProductCache();
                                                            // Header wins over cookie on subsequent API calls — update before refetch.
                                                            if (s.customer_uid) {
                                                                Axios.defaults.headers["X-Emailwish-Customer-UID"] = s.customer_uid;
                                                            }
                                                            const userRes = await new UserAPIs().fetch_user();
                                                            if (!UserAPIs.hasError(userRes)) {
                                                                await new CommonApis().after_login(userRes, dispatch);
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to switch shop", err);
                                                        }
                                                        handleClose();
                                                    }}
                                                >
                                                    <ShopSwitcherLogoOrInitials
                                                        name={s.name}
                                                        colorKey={
                                                            s.shop_owner_email ||
                                                            s.myshopify_domain ||
                                                            String(s.uid ?? "")
                                                        }
                                                        logoUrl={
                                                            active ? signatureLogoUrl : null
                                                        }
                                                        variant="list"
                                                    />
                                                    <span className="ew-shop-switcher__shop-name-text">
                                                        {s.name}
                                                    </span>
                                                </button>
                                                {storeUrl ? (
                                                    <a
                                                        href={storeUrl}
                                                        rel="noreferrer noopener"
                                                        target="_blank"
                                                        className="ew-shop-switcher__external"
                                                        aria-label={`Open ${s.name} storefront in new tab`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <OpenInNew fontSize="small" />
                                                    </a>
                                                ) : (
                                                    <span className="ew-shop-switcher__external ew-shop-switcher__external--placeholder" aria-hidden />
                                                )}
                                            </div>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="ew-dropdown-option ew-shop-switcher__add"
                                        onClick={() => {
                                            handleClose();
                                            history.push("/add-store");
                                        }}
                                    >
                                        <Add className="ew-shop-switcher__add-icon" fontSize="small" />
                                        {tCommon("shopSwitcher.addNewShop")}
                                    </button>
                                </nav>
                            </Popover>
                        )}
                    </div>
                }
                </Col>
                <Col md={12} xs={12} className="d-flex align-items-center justify-content-center menu-parent-div">
                    <div className="list-col w-100">
                        <div className="vertical-center w-100">
                            <List component="nav" className="sideMenu">
                                {links.map((i, index) => {
                                    let isActive = isSubMenuActive(location.pathname, i.link);
                                    let isHidden = i.link == "/email/your-server" && hideServer;
                                    
                                    if (isHidden) return null;

                                    return (
                                        <ListItemLink
                                            className={"sub-menu " + (isActive ? "active" : "")}
                                            key={index}
                                            to={i.link}
                                            data-tut={i["data-tut"]}
                                        >
                                            <ListItemIcon
                                                style={
                                                    isActive
                                                        ? {
                                                            minWidth: "40px",
                                                            minHeight: "40px",
                                                            background: "black",
                                                            color: "white",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: "10px",
                                                            marginRight:"10px",
                                                            boxShadow: "5px 15px 12px rgba(0, 0, 0, 0.2)",
                                                        }
                                                        : {
                                                            minWidth: "40px", // Apply default styling if not active
                                                            minHeight: "40px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            marginRight: "10px",
                                                        }
                                                }
                                            >
                                                {isActive && i.active_node}
                                                {(!isActive || !i.active_node) && i.svg_node}
                                            </ListItemIcon>
                                            <ListItemText   
                                                primary={
                                                    <div
                                                        className={"list-text"}
                                                        style={{
                                                            width: 90,
                                                            paddingLeft: "4px",
                                                            whiteSpace: "initial",
                                                            color: "black",
                                                        }}
                                                    >
                                                        {i.text}
                                                    </div>
                                                }
                                            />
                                        </ListItemLink>
                                    );
                                })}
                            </List>

                        </div>
                    </div>
                    {location.pathname === '/chats' && (
                        <div
                            className={classNames(
                                "selector-div-new mt-3 sidebar-chat-promo-wrap",
                                chatPromoCollapsed && "sidebar-chat-promo-wrap--collapsed"
                            )}
                            style={{ padding: '0 16px', paddingBottom: '20px' }}
                        >
                            <button
                                type="button"
                                className={classNames(
                                    "sidebar-chat-promo__toggle widget-setting-select widget-setting-select--menu-top",
                                    !chatPromoCollapsed && "widget-setting-select--open"
                                )}
                                aria-expanded={!chatPromoCollapsed}
                                aria-label={chatPromoCollapsed ? tChats("promo.show") : tChats("promo.hide")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setChatPromoCollapsed((collapsed) => !collapsed);
                                }}
                            >
                                <span className="widget-setting-select__chevron" aria-hidden />
                            </button>
                            <div
                                className="sidebar-chat-promo"
                                style={{
                                    backgroundColor: '#0052FF',
                                    borderRadius: '16px',
                                    padding: '24px 20px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    color: 'white',
                                    boxShadow: '0 4px 12px rgba(0, 82, 255, 0.2)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    margin: '0 auto',
                                    maxWidth: '100%',
                                    minHeight: '260px',
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => {
                                    if (chatPromoCollapsed) {
                                        setChatPromoCollapsed(false);
                                        return;
                                    }
                                    window.open('https://play.google.com/store/apps/details?id=com.emailwish.customer', '_blank');
                                }}
                            >
                                {/* Top right arrow button */}
                                <div style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#0f172a',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="6" y1="18" x2="16" y2="8"></line>
                                        <polyline points="11 8 16 8 16 13"></polyline>
                                    </svg>
                                </div>

                                {/* Background Pattern Circles */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: '-20px',
                                    left: '-20px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 50px)',
                                    gridTemplateRows: 'repeat(3, 50px)',
                                    gap: '2px',
                                    zIndex: 1,
                                    opacity: 0.9
                                }}>
                                    <div style={{gridColumn: '1', gridRow: '1', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '1', gridRow: '2', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '2', gridRow: '2', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '1', gridRow: '3', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '2', gridRow: '3', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '3', gridRow: '3', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{gridColumn: '4', gridRow: '3', width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                                    <div style={{
                                        gridColumn: '3', gridRow: '2', 
                                        width: '50px', height: '50px', 
                                        backgroundColor: '#B3CCFF', 
                                        borderBottomLeftRadius: '50px', 
                                        borderBottomRightRadius: '50px',
                                        clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                        transform: 'translateY(-25px)'
                                    }}></div>
                                </div>

                                <div style={{ 
                                    position: 'relative', 
                                    zIndex: 2, 
                                    width: '100%', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-start',
                                    gap: '4px'
                                }}>
                                    <h4 style={{
                                        fontSize: '19px',
                                        fontWeight: '600',
                                        margin: '0',
                                        lineHeight: '1.4'
                                    }}>
                                        {tChats("promo.titleTop")}<br />
                                        {tChats("promo.titleBottom")}
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        marginBottom: '16px', 
                                        fontSize: '19px', 
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <span>{tChats("promo.onYour")}</span>
                                        <span style={{ margin: '0 6px' }}>→</span>
                                        <span style={{ 
                                            backgroundColor: '#E6EFFF', 
                                            color: 'black', 
                                            padding: '0 4px',
                                            lineHeight: '1.2',
                                            display: 'inline-block'
                                        }}>{tChats("promo.phone")}</span>
                                    </div>

                                    <a href="https://play.google.com/store/apps/details?id=com.emailwish.customer" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                        <img src={playStore} alt={tChats("promo.playStoreAlt")} style={{ width: '130px', borderRadius: '6px' }} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                </Col>
                {/* </Row> 
                <Row> */}

                {/* {location.pathname === '/chats' && (
                    <Col md={12} xs={12} className="d-flex align-items-center justify-content-center banner-parent-div">
                        <div className={"selector-div-new mt-3"}>
                            <div className="selected_dropdown">
                                <BannerSide style={{ cursor: 'pointer' }}>
                                    <img className="rounded-lg" src={banner} style={{ width: '100%;' }} alt="banner" />
                                    <div style={{ display: 'block', padding: '10px' }}>
                                        <p className="text-white" style={{ textAlign: 'center' }}>
                                            Chat With Customers on your Phone
                                        </p>
                                        <a href="https://play.google.com/store/apps/details?id=com.emailwish.customer" target="__blank" onClick={(e) => e.stopPropagation()} ><img className="rounded-lg" src={playStore} style={{ width: '100%;' }} alt="banner" /></a>
                                    </div>

                                </BannerSide>
                            </div>
                        </div>
                    </Col>
                )} */}
            </Row>
        </div>
        <div id="content">
            {
                props.children
            }
        </div>

    </div>;
}

export default Sidebar;
